import { AppApi, ScreenView, el, panel, button, jellyAmount } from '../ui/app-api'
import type { ShopItemData } from '../../types'
import { slotOf } from '../../data/cosmetics'

export function createShopScreen(api: AppApi): ScreenView {
  const root = el('div', { class: 'screen' })
  const p = panel('Shop', true)
  const balLine = el('div', { class: 'center' })
  const list = el('div', { class: 'scroll-area', style: { maxHeight: '52vh' } })
  const back = button('BACK', { variant: 'secondary', onClick: () => api.go('menu') })
  p.append(balLine, list, back)
  root.append(p)

  let items: ShopItemData[] = []

  const refreshBalance = () => {
    balLine.replaceChildren(el('span', { class: 'muted', text: 'Balance: ' }), jellyAmount(api.economy.state.jelly))
  }

  const render = () => {
    refreshBalance()
    list.replaceChildren()
    for (const item of items) {
      const owned = api.economy.isUnlocked(item.id)
      const afford = api.economy.state.jelly >= item.cost
      const row = el('div', { class: owned ? 'row is-owned' : 'row' })
      const main = el(
        'div',
        { class: 'row-main' },
        el('span', { class: 'row-name', text: item.name }),
        el('span', { class: 'row-desc', text: item.description }),
      )
      const right = el('div', { class: 'row-actions' })
      right.append(el('span', { class: 'row-cost', text: `${item.cost}` }))
      if (owned) {
        const slot = slotOf(item.id)
        if (slot) {
          const eq = api.economy.isEquipped(item.id)
          right.append(
            button(eq ? 'EQUIPPED ✓' : 'EQUIP', {
              sm: true,
              variant: eq ? 'primary' : 'secondary',
              onClick: () => doEquip(item),
            }),
          )
        } else {
          right.append(el('span', { class: 'muted', text: 'Owned ✓' }))
        }
      } else {
        const buy = button('BUY', { sm: true, onClick: () => doBuy(item) })
        if (!afford) {
          buy.classList.add('is-disabled')
          ;(buy as HTMLButtonElement).disabled = true // not just greyed — actually inert
        }
        right.append(buy)
      }
      row.append(main, right)
      list.append(row)
    }
  }

  let purchasing = false
  const doBuy = async (item: ShopItemData) => {
    if (purchasing) return // ignore re-clicks while a purchase is in flight
    if (api.economy.state.jelly < item.cost) {
      api.toast('Not enough jelly yet!') // live re-check — guards a stale-enabled button
      return
    }
    purchasing = true
    api.audio.play('click')
    try {
      await api.economy.purchase(item.id)
      api.toast(`Unlocked ${item.name}!`)
      api.audio.play('levelclear')
      render()
    } catch (e) {
      api.toast(e instanceof Error ? e.message : 'Purchase failed')
    } finally {
      purchasing = false
    }
  }

  /** Toggle equip for an owned cosmetic (equipping replaces whatever was in its slot). */
  const doEquip = (item: ShopItemData) => {
    const slot = slotOf(item.id)
    if (!slot) return
    const wasEquipped = api.economy.isEquipped(item.id)
    api.economy.setEquipped(slot, wasEquipped ? null : item.id)
    api.audio.play('click')
    api.toast(wasEquipped ? `Removed ${item.name}` : `Equipped ${item.name}!`)
    render() // reflect the new equipped state on every row in this slot
  }

  let showToken = 0

  return {
    el: root,
    async onShow() {
      const token = ++showToken
      list.replaceChildren(el('p', { class: 'muted center', text: 'Loading shop…' }))
      refreshBalance()
      try {
        const res = await api.economy.shop()
        if (token !== showToken) return // a newer onShow superseded this fetch
        items = res as ShopItemData[]
        render()
      } catch {
        if (token !== showToken) return
        list.replaceChildren(el('p', { class: 'muted center', text: 'Shop unavailable — server offline.' }))
      }
    },
  }
}
